import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fmt } from './oilApi';
import { OilIcon } from './OilIcons';
import OilSpyReport from './OilSpyReport';
import { music, sfxClose } from './oilSounds';

const OUTCOME = {
  SUCCESS_HIT: { title: 'OPERAÇÃO BEM-SUCEDIDA', sub: 'O alvo sentiu no bolso.',
    cls: 'from-emerald-900/70 to-black border-emerald-600', tag: 'text-emerald-300' },
  SUCCESS_BLOCKED: { title: 'O ESCUDO SEGUROU', sub: 'Seu pessoal chegou, mas a proteção aguentou.',
    cls: 'from-sky-900/70 to-black border-sky-600', tag: 'text-sky-300' },
  SUCCESS: { title: 'DOSSIÊ NA MÃO', sub: 'Seus informantes voltaram com tudo.',
    cls: 'from-amber-900/70 to-black border-amber-600', tag: 'text-amber-300' },
  FAILED: { title: 'MISSÃO FRACASSADA', sub: 'Sua equipe voltou de mãos vazias.',
    cls: 'from-zinc-900 to-black border-zinc-700', tag: 'text-zinc-300' },
};

const FADE = 700; // fade de entrada/saída do clipe

const Line = ({ label, children, testid }) => (
  <div className="flex items-center justify-between gap-2 border-b border-white/5 py-1.5 last:border-0"
    data-testid={testid}>
    <span className="text-[9px] font-black uppercase tracking-wider text-zinc-500">{label}</span>
    <span className="text-right text-[11px] font-black text-zinc-100">{children}</span>
  </div>
);

export const OilOperationResult = ({ data, svc, target, video, services, limits, onClose }) => {
  const success = ['SUCCESS_HIT', 'SUCCESS_BLOCKED'].includes(data.status)
    || (data.status === 'SUCCESS' && data.report);
  const clip = success ? video?.success : video?.fail;
  const [phase, setPhase] = useState(clip ? 'video' : 'card');
  const [fading, setFading] = useState(false);
  const [entered, setEntered] = useState(false);
  const ref = useRef(null);
  const out = OUTCOME[data.status] || OUTCOME.FAILED;

  useEffect(() => {
    if (phase !== 'video' || !ref.current) return;
    const v = ref.current;
    music.duck(true);
    const toCard = () => setPhase('card');
    // fade-out no fim: escurece a tela e baixa o áudio do clipe
    const onTime = () => {
      if (!v.duration) return;
      const left = (v.duration - v.currentTime) * 1000;
      if (left <= FADE) {
        setFading(true);
        v.volume = Math.max(0, left / FADE);
      }
    };
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('error', toCard);
    v.play().catch(() => { v.muted = true; v.play().catch(toCard); });
    const t = setTimeout(toCard, 22000);
    const enter = setTimeout(() => setEntered(true), 60);
    return () => {
      clearTimeout(t); clearTimeout(enter);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('error', toCard);
      music.duck(false);
    };
  }, [phase]);

  const skip = () => {
    setFading(true);
    if (ref.current) ref.current.volume = 0;
    setTimeout(() => setPhase('card'), FADE * 0.6);
  };

  if (phase === 'video') {
    return createPortal(
      <div className="fixed inset-0 z-[99] flex items-center justify-center bg-black"
        data-testid="oil-op-video-overlay">
        <video ref={ref} playsInline autoPlay preload="auto"
          data-testid="oil-op-video" onEnded={() => setPhase('card')}
          className="max-h-full w-full object-contain">
          <source src={`${clip}.webm`} type="video/webm" />
          <source src={`${clip}.mp4`} type="video/mp4" />
        </video>
        <div data-testid="oil-op-video-fade"
          className="pointer-events-none absolute inset-0 bg-black"
          style={{ opacity: !entered || fading ? 1 : 0, transition: `opacity ${FADE}ms ease-in-out` }} />
        <button onClick={skip} data-testid="oil-op-video-skip"
          className="absolute bottom-6 right-4 rounded-full border border-amber-600/70 bg-black/80 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-amber-300 active:scale-95">
          Pular ▸
        </button>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[99] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      data-testid="oil-op-result" onClick={onClose}
      style={{ animation: `op-fade-in ${FADE}ms ease-out` }}>
      <style>{`@keyframes op-fade-in { from { opacity: 0 } to { opacity: 1 } }`}</style>
      <div className="w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-[2px]" data-testid={`oil-op-neon-${success ? 'success' : 'fail'}`}
          style={{
            background: 'linear-gradient(160deg, #f2d387, #6e5118 40%, #c99b3f 70%, #f2d387)',
            borderRadius: '18px',
            animation: `${success ? 'op-neon-green' : 'op-neon-red'} 1.6s ease-in-out infinite`,
          }}>
          <style>{`
            @keyframes op-neon-green {
              0%, 100% { box-shadow: 0 0 6px rgba(16,220,120,0.45); }
              50% { box-shadow: 0 0 20px rgba(60,255,160,0.95), 0 0 42px rgba(16,200,110,0.55); }
            }
            @keyframes op-neon-red {
              0%, 100% { box-shadow: 0 0 6px rgba(255,40,40,0.45); }
              50% { box-shadow: 0 0 20px rgba(255,80,80,0.95), 0 0 42px rgba(200,0,0,0.55); }
            }
          `}</style>
          <div className={`rounded-[16px] border bg-gradient-to-b p-4 ${out.cls}`}>
            <p className={`text-center text-[9px] font-black uppercase tracking-[0.25em] ${out.tag}`}>
              {svc?.name}
            </p>
            <p className="mt-1 text-center text-base font-black uppercase text-white"
              data-testid="oil-op-title">{out.title}</p>
            <p className="mb-2 text-center text-[10px] font-bold text-zinc-300">{out.sub}</p>

            {target && (
              <div className="mb-2 flex items-center gap-2 rounded-xl border border-white/10 bg-black/50 p-2">
                <span className="h-9 w-9 shrink-0 overflow-hidden rounded-full border border-amber-800/70 bg-black">
                  {target.avatar
                    ? <img src={target.avatar} alt="" className="h-full w-full object-cover" />
                    : <span className="flex h-full w-full items-center justify-center text-base">🦊</span>}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[9px] font-black uppercase tracking-wider text-zinc-500">Alvo</span>
                  <span className="block truncate text-[11px] font-black text-amber-100">{target.nickname}</span>
                </span>
                {target.rank ? (
                  <span className="shrink-0 rounded-full border border-amber-800/70 bg-amber-950/50 px-2 py-1 text-[10px] font-black text-amber-300">
                    {target.rank}º
                  </span>
                ) : null}
              </div>
            )}

            <div className="rounded-xl border border-white/10 bg-black/50 px-2.5 py-1">
              {data.damage > 0 && (
                <Line label="Prejuízo causado" testid="oil-op-damage">
                  <span className="text-red-400">{fmt(data.damage)}</span> FOX Dólar
                </Line>
              )}
              <Line label="Respeito ganho" testid="oil-op-respect">
                <OilIcon k="respect" /> <span className="text-rose-300">+{fmt(data.respect || 0)}</span>
              </Line>
              <Line label="Custo do serviço" testid="oil-op-cost">
                <OilIcon k="black" /> <span className="text-amber-300">{fmt(svc?.cost_black_coins)}</span>
              </Line>
            </div>

            {data.message && (
              <p className="mt-2 rounded-xl bg-black/40 px-3 py-2 text-[10px] font-bold leading-snug text-zinc-200"
                data-testid="oil-op-message">{data.message}</p>
            )}

            {data.facility?.points > 0 && (
              <div className="mt-2 rounded-xl border border-orange-700/70 bg-black/60 p-2.5 text-center"
                data-testid="oil-op-facility">
                <p className="text-[9px] font-black uppercase tracking-wider text-zinc-500">
                  {data.facility.facility === 'refinery' ? 'Usina de Perfuração' : 'Envasadora'} LV{data.facility.level}
                </p>
                <p className={`mt-0.5 text-lg font-black ${data.facility.broken ? 'text-red-400' : 'text-orange-300'}`}>
                  −{data.facility.points} de integridade
                </p>
                <p className="text-[10px] font-bold text-zinc-300">
                  {data.facility.broken
                    ? 'A instalação PAROU: ele só volta a produzir depois de pagar o reparo.'
                    : `Restam ${data.facility.integrity} de 100 na instalação dele.`}
                </p>
              </div>
            )}

            {data.cargo && (
              <div className="mt-2 rounded-xl border border-amber-600/60 bg-black/60 p-2.5 text-center"
                data-testid="oil-op-cargo">
                {data.cargo.barrels?.length ? (
                  <>
                    {data.cargo.premium && (
                      <p className="text-[9px] font-black uppercase tracking-[0.25em] text-amber-300"
                        style={{ animation: 'op-neon-green 1.4s ease-in-out infinite' }}
                        data-testid="oil-op-cargo-premium">Carga nobre</p>
                    )}
                    <p className="text-[9px] font-black uppercase tracking-wider text-zinc-500">Carga roubada</p>
                    <div className="mt-1 flex flex-wrap items-end justify-center gap-2">
                      {data.cargo.barrels.map((lv, i) => (
                        <span key={i} className="flex flex-col items-center">
                          <img src={`/oil/barril_lv${lv}.webp`} alt="" className="h-12 w-auto drop-shadow-[0_0_10px_rgba(245,158,11,0.5)]" />
                          <span className="text-[9px] font-black text-amber-300">LV{lv}</span>
                        </span>
                      ))}
                    </div>
                    <p className="mt-1 text-[10px] font-bold text-amber-100">
                      {data.cargo.barrels.length} barril(is) no seu galpão
                      {data.cargo.fox_value ? ` · vale ${fmt(data.cargo.fox_value)} FOX Dólar` : ''}
                    </p>
                  </>
                ) : data.cargo.drops ? (
                  <p className="text-[10px] font-bold text-sky-200" data-testid="oil-op-cargo-drops">
                    O caminhão levava só o bruto: <OilIcon k="drops" /> <span className="font-black text-sky-300">+{fmt(data.cargo.drops)}</span> gotas
                  </p>
                ) : data.cargo.capped ? (
                  <p className="text-[10px] font-bold text-zinc-400" data-testid="oil-op-cargo-capped">
                    O galpão deste alvo já foi saqueado o suficiente hoje. Nenhuma carga saiu.
                  </p>
                ) : (
                  <p className="text-[10px] font-bold text-zinc-400" data-testid="oil-op-cargo-empty">
                    Galpão vazio: não havia carga nem bruto para levar.
                  </p>
                )}
              </div>
            )}

            {data.report && (
              <OilSpyReport report={data.report} services={services} limits={limits} />
            )}

            <button onClick={() => { sfxClose(); onClose(); }} data-testid="oil-op-close"
              className="mt-3 w-full rounded-xl bg-gradient-to-b from-amber-400 to-amber-700 py-2.5 text-xs font-black uppercase tracking-wider text-black active:scale-[0.98]">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OilOperationResult;
